import { create } from "zustand";
import type { ChatMessage, SourceAttribution } from "@/types/paper";

interface ChatState {
  messages: ChatMessage[];
  isStreaming: boolean;
  sources: SourceAttribution[];
  sourcesMap: Record<number, SourceAttribution[]>;
  error: string | null;

  addUserMessage: (content: string) => void;
  startStreaming: () => void;
  appendToken: (token: string) => void;
  completeStreaming: (sources: SourceAttribution[]) => void;
  failStreaming: (error: string) => void;
  clearChat: () => void;
  loadMessages: (
    msgs: Array<{
      id?: string;
      role: "user" | "assistant";
      content: string;
      sources?: SourceAttribution[];
    }>,
  ) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  isStreaming: false,
  sources: [],
  sourcesMap: {},
  error: null,

  addUserMessage: (content) =>
    set((state) => ({
      messages: [...state.messages, { role: "user", content }],
      error: null,
    })),

  startStreaming: () =>
    set((state) => ({
      isStreaming: true,
      error: null,
      sources: [],
      messages: [...state.messages, { role: "assistant", content: "" }],
    })),

  appendToken: (token) =>
    set((state) => {
      const msgs = [...state.messages];
      const last = msgs[msgs.length - 1];
      if (last?.role === "assistant") {
        msgs[msgs.length - 1] = { ...last, content: last.content + token };
      }
      return { messages: msgs };
    }),

  completeStreaming: (sources) =>
    set((state) => {
      const lastIdx = state.messages.length - 1;
      const newMap = { ...state.sourcesMap };
      if (sources.length > 0 && lastIdx >= 0) {
        newMap[lastIdx] = sources;
      }
      return { isStreaming: false, sources, sourcesMap: newMap };
    }),

  failStreaming: (error) =>
    set({ isStreaming: false, error }),

  clearChat: () =>
    set({
      messages: [],
      isStreaming: false,
      sources: [],
      sourcesMap: {},
      error: null,
    }),

  loadMessages: (msgs) => {
    const sourcesMap: Record<number, SourceAttribution[]> = {};
    const messages: ChatMessage[] = msgs.map((m, i) => {
      if (m.sources && m.sources.length > 0) {
        sourcesMap[i] = m.sources;
      }
      return { role: m.role, content: m.content, id: m.id };
    });
    // Set sources to the last assistant message's sources (for SourcesList compat)
    const lastAssistantIdx = [...messages]
      .reverse()
      .findIndex((m) => m.role === "assistant");
    const lastSources =
      lastAssistantIdx >= 0
        ? sourcesMap[messages.length - 1 - lastAssistantIdx] ?? []
        : [];

    set({
      messages,
      sourcesMap,
      sources: lastSources,
      isStreaming: false,
      error: null,
    });
  },
}));
